import {
  BulkInvitationColumnSpec,
  BulkInvitationRowError,
  BulkInvitationRowValues,
  readText,
} from "legal-entities/invitations/bulk/bulk-invitation-column-spec";

/**
 * The address parts, in the order the row editor shows them. Any one of them satisfies the API:
 * `Address.FromStrings` refuses an address with every field empty, so a roster that knows only the
 * country is enough to create the firm's legal entity.
 */
export const TEAM_LEADER_ADDRESS_KEYS = ["address1", "city", "state", "postalCode", "country"] as const;

/**
 * A client's list of the firms it wants to keep working with: one team leader per firm.
 *
 * This is the list `070-3p-process.md` calls the one the whole roll-out runs from, and the one it
 * says usually takes longest to get - which is why it arrives as a sheet rather than as thirty
 * separate decisions.
 */
const teamLeaderRosterColumns: BulkInvitationColumnSpec = {
  id: "team-leaders",
  noun: "team leader",
  templateFileName: "attorney-hub-team-leaders",
  templateSheetName: "Team leaders",
  fields: [
    {
      key: "email",
      header: "Email address",
      kind: "email",
      required: true,
      hint: "Where the invitation is sent. One per firm.",
      example: "jane.doe@example.invalid",
    },
    { key: "firstName", header: "First name", kind: "text", required: true, example: "Jane" },
    { key: "lastName", header: "Last name", kind: "text", required: true, example: "Doe" },
    {
      key: "title",
      header: "Title",
      kind: "text",
      required: true,
      hint: "How they are addressed at the firm, such as Managing Partner.",
      example: "Managing Partner",
    },
    {
      key: "entityName",
      header: "Firm name",
      kind: "text",
      required: true,
      hint: "Ignored for a firm already on Attorney Hub, which has a name of its own.",
      example: "Example & Partners LLP",
    },
    { key: "address1", header: "Firm address", kind: "text", required: false, example: "1 Example Street" },
    { key: "city", header: "City", kind: "text", required: false, example: "Springfield" },
    { key: "state", header: "State or province", kind: "text", required: false, example: "Illinois" },
    { key: "postalCode", header: "Postal code", kind: "text", required: false, example: "62701" },
    {
      key: "country",
      header: "Country",
      kind: "text",
      required: false,
      hint: "Any one part of the address will do. All of them empty will not.",
      example: "United States",
    },
    {
      key: "message",
      header: "Message",
      kind: "text",
      required: false,
      hint: "Added to the letter this firm receives. Optional.",
      example: "We look forward to working with you on our matters.",
    },
  ],
  // 1,018px with the actions column, which fits the 1,046px pane beside the navigation at a 1366
  // window; the Problem column's 180 more still fits at 1536. Email and name stay in view when it
  // does scroll, so a row being fixed is never one whose owner has scrolled away.
  gridColumns: [
    { id: "email", header: "Email address", keys: ["email"], size: 190, pinned: true },
    { id: "name", header: "Name", keys: ["firstName", "lastName"], size: 130, pinned: true },
    { id: "title", header: "Title", keys: ["title"], size: 120 },
    { id: "entityName", header: "Firm name", keys: ["entityName"], size: 150 },
    { id: "address", header: "Firm address", keys: TEAM_LEADER_ADDRESS_KEYS, separator: ", ", size: 180 },
    { id: "message", header: "Message", keys: ["message"], size: 160, maxLines: 2 },
  ],
  validateRow: (row: BulkInvitationRowValues): readonly BulkInvitationRowError[] => {
    const hasAnyAddress = TEAM_LEADER_ADDRESS_KEYS.some((key) => readText(row, key).length > 0);
    if (hasAnyAddress) return [];

    // Marked on the first address field rather than on all five, so the row editor puts one
    // message under one input instead of repeating itself down the form.
    return [
      {
        key: "address1",
        message: "Some part of the firm's address is required - a country on its own is enough.",
      },
    ];
  },
};

export default teamLeaderRosterColumns;
